import React, { useState, useMemo, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Container, Row, Col, InputGroup, FormControl, Badge, Button } from 'react-bootstrap';
import {
  HiSearch,
  HiX,
  HiSparkles,
  HiArrowNarrowRight,
  HiFire,
  HiArrowLeft
} from './components/Icons';
import SEO from './components/SEO';
import companiesData from './companies.json';
import { CompanyType } from './types';
import { getCategoryBySlug, CATEGORY_META } from './utils/categories';
import './Category.css';
import ReactGA from 'react-ga4';

const companies: CompanyType[] = companiesData;

function Category() {
  const { categorySlug } = useParams<{ categorySlug: string }>();
  const [search, setSearch] = useState('');

  const categoryMeta = useMemo(() => {
    return categorySlug ? getCategoryBySlug(categorySlug) : undefined;
  }, [categorySlug]);

  useEffect(() => {
    if (typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch (e) {
        // Safe fallback in tests
      }
    }
    ReactGA.send({ hitType: 'pageview', page: window.location.pathname + window.location.search });
  }, [categorySlug]);

  // Filter companies matching this category and optional search term
  const categoryCompanies = useMemo(() => {
    if (!categoryMeta) return [];
    return companies.filter(company => {
      const isInCategory = company.category === categoryMeta.name;
      if (!isInCategory) return false;

      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return (
        company.name.toLowerCase().includes(q) ||
        company.coupon.value.toLowerCase().includes(q) ||
        company.coupon.description.toLowerCase().includes(q)
      );
    });
  }, [categoryMeta, search]);

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const structuredData = useMemo(() => {
    if (!categoryMeta) return undefined;
    const pageUrl = `https://couponshowcase.com/kategorie/${categoryMeta.slug}`;

    const items = categoryCompanies.map((c, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: `${c.name} - ${c.coupon.value}`,
      url: `https://couponshowcase.com/${c.name.replace(/\s+/g, '_').toLowerCase()}`
    }));

    const result: Array<Record<string, any>> = [
      {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        '@id': `${pageUrl}#webpage`,
        url: pageUrl,
        name: `${categoryMeta.title} | Coupon Showcase`,
        description: categoryMeta.description,
        inLanguage: 'de-DE'
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Startseite',
            item: 'https://couponshowcase.com/'
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Kategorien',
            item: 'https://couponshowcase.com/#categories'
          },
          {
            '@type': 'ListItem',
            position: 3,
            name: categoryMeta.name,
            item: pageUrl
          }
        ]
      },
      {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: categoryMeta.title,
        description: categoryMeta.description,
        itemListElement: items
      }
    ];

    if (categoryMeta.faqs && categoryMeta.faqs.length > 0) {
      result.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: categoryMeta.faqs.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      });
    }

    return result;
  }, [categoryMeta, categoryCompanies]);

  if (!categoryMeta) {
    return (
      <div className="category-page py-5">
        <SEO
          title="Kategorie nicht gefunden | Coupon Showcase"
          description="Die gesuchte Gutschein-Kategorie existiert leider nicht."
          canonical="/"
        />
        <Container>
          <div className="empty-category-card text-center p-5 mx-auto">
            <div className="empty-icon mb-3">📂</div>
            <h2 className="mb-2">Kategorie nicht gefunden</h2>
            <p className="text-muted mb-4">
              Die angeforderte Kategorie konnte leider nicht gefunden werden.
            </p>
            <Link to="/" className="btn btn-primary px-4 py-2">
              <HiArrowLeft className="me-1" /> Zurück zur Startseite
            </Link>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="category-page">
      <SEO
        title={`${categoryMeta.title} | Coupon Showcase`}
        description={categoryMeta.description}
        canonical={`/kategorie/${categoryMeta.slug}`}
        keywords={categoryMeta.keywords}
        structuredData={structuredData}
      />

      {/* Category Hero Section */}
      <section className="category-hero-section">
        <div className="category-hero-glow"></div>
        <Container>
          {/* Breadcrumb Navigation */}
          <div className="category-breadcrumb mb-3">
            <Link to="/" className="breadcrumb-link">Startseite</Link>
            <span className="breadcrumb-sep">/</span>
            <Link to="/#categories" className="breadcrumb-link">Kategorien</Link>
            <span className="breadcrumb-sep">/</span>
            <strong className="breadcrumb-current">{categoryMeta.name}</strong>
          </div>

          <div className="category-hero-content text-center">
            <div className="category-pill-badge mb-3">
              <HiSparkles className="text-warning me-1" /> {categoryCompanies.length} GEPRÜFTE ANGEBOTE
            </div>
            <h1 className="category-hero-title">{categoryMeta.title}</h1>
            <p className="category-hero-subtitle">{categoryMeta.description}</p>

            {/* In-category search bar */}
            <div className="category-search-container mt-4">
              <InputGroup className="category-search-box">
                <InputGroup.Text className="search-icon-addon">
                  <HiSearch />
                </InputGroup.Text>
                <FormControl
                  placeholder={`${categoryMeta.name} Gutscheine & Codes durchsuchen...`}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="search-input-field"
                  aria-label="Kategorie Gutschein Suche"
                />
                {search && (
                  <Button
                    variant="link"
                    className="clear-search-btn"
                    onClick={() => setSearch('')}
                    aria-label="Suche zurücksetzen"
                  >
                    <HiX />
                  </Button>
                )}
              </InputGroup>
            </div>
          </div>
        </Container>
      </section>

      <Container className="category-main-content py-5">
        {/* Category Switcher Pills */}
        <div className="category-nav-bar mb-4">
          <Link to="/" className="cat-nav-pill">
            Alle Deals
          </Link>
          {Object.values(CATEGORY_META).map(cat => {
            const isActive = cat.slug === categoryMeta.slug;
            return (
              <Link
                key={cat.slug}
                to={`/kategorie/${cat.slug}`}
                className={`cat-nav-pill ${isActive ? 'active' : ''}`}
              >
                {cat.name}
              </Link>
            );
          })}
        </div>

        {/* Deals Grid */}
        {categoryCompanies.length > 0 ? (
          <Row className="g-4">
            {categoryCompanies.map((company, index) => {
              const companySlug = company.name.replace(/\s+/g, '_').toLowerCase();
              return (
                <Col lg={4} md={6} sm={12} key={index}>
                  <div className="deal-card">
                    <div className="card-top-bar">
                      <div
                        className="brand-avatar"
                        style={{ background: company.color || '#4f46e5' }}
                      >
                        {getInitials(company.name)}
                      </div>
                      <div className="card-tags">
                        {company.featured && (
                          <span className="featured-badge">
                            <HiFire className="me-1" /> Top Deal
                          </span>
                        )}
                        <Badge bg="light" text="dark" className="category-badge">
                          {company.category || 'Gutschein'}
                        </Badge>
                      </div>
                    </div>

                    <div className="card-content">
                      <h3 className="company-title">{company.name}</h3>

                      <div className="deal-value-pill">
                        <span className="value-label">Vorteil:</span>
                        <span className="value-text">{company.coupon.value}</span>
                      </div>

                      <p className="card-description">{company.coupon.description}</p>
                    </div>

                    <div className="card-action-footer">
                      <span className="action-text">
                        {company.coupon.code ? 'Gutscheincode anzeigen' : 'Angebot einlösen'}
                      </span>
                      <span className="action-arrow">
                        <HiArrowNarrowRight />
                      </span>
                    </div>

                    {/* Overlay Clickable Link */}
                    <Link
                      to={`/${companySlug}`}
                      className="stretched-link"
                      aria-label={`${company.name} Gutschein anzeigen`}
                    />
                  </div>
                </Col>
              );
            })}
          </Row>
        ) : (
          <div className="empty-search-state text-center py-5">
            <div className="empty-icon-wrapper mb-3">
              <HiSearch className="empty-search-icon" />
            </div>
            <h3 className="empty-title">Keine Angebote für "{search}" in dieser Kategorie</h3>
            <Button
              variant="primary"
              className="mt-3 px-4 py-2"
              onClick={() => setSearch('')}
            >
              Alle {categoryMeta.name} Angebote anzeigen
            </Button>
          </div>
        )}

        {/* Category FAQ Section */}
        {categoryMeta.faqs && categoryMeta.faqs.length > 0 && (
          <section className="faq-section mt-5 pt-4 pb-3" id="category-faq">
            <div className="section-heading-wrapper text-center mb-4">
              <h2 className="section-title">Häufige Fragen zu {categoryMeta.name}</h2>
              <p className="text-muted">Alles Wichtige über Gutscheine, Rabattcodes und Boni in dieser Kategorie.</p>
            </div>
            <Row className="justify-content-center">
              <Col lg={10}>
                <div className="faq-grid">
                  {categoryMeta.faqs.map((faq, idx) => (
                    <div className="faq-card" key={idx}>
                      <h3 className="faq-question">{faq.question}</h3>
                      <p className="faq-answer">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </Col>
            </Row>
          </section>
        )}
      </Container>
    </div>
  );
}

export default Category;
